import { lazy } from 'react'

/**
 * One lazy component per path.
 *
 * `lazy()` must be called once per module — never per render/item. Each call
 * creates a NEW component type that suspends on its first render (even when
 * the module is already loaded), so an uncached `preload` inside a list's
 * `createItems` makes every cell of every row suspend individually and —
 * worse — changes the cells' component identity whenever items are rebuilt,
 * remounting the whole list. Caching by path runs each module's suspense
 * cycle exactly once and keeps component identity stable across rebuilds.
 *
 * @type {Map<string, React.LazyExoticComponent<React.ComponentType<any>>>}
 */
const lazyComponents = new Map()

/**
 * Lazy-loads the element from `path`.
 *
 * @param {string} path the path to the element to load
 * @returns {React.LazyExoticComponent<React.ComponentType<any>>}
 */
export default (path) => {
  let component = lazyComponents.get(path)

  if (!component) {
    component = lazy(() => import(`@ui/${path}`))
    lazyComponents.set(path, component)
  }

  return component
}
